// Отметка «хороших моментов» в окне отсмотра записи урока (OtborTab → ReviewModal).
// Сопроводитель смотрит запись и отмечает куски — как преподаватель объясняет,
// мотивирует, рассказывает о себе. Лучшие пойдут на карточку препода и в соцсети.
// Логика — хук useLessonMoments, интерфейс — три куска: полоска времени под плеером,
// две кнопки «Начало/Конец момента» и вкладка справа. API: /api/moments (routes/moments.js).
import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../api'

export function fmtT(s) {
  s = Math.max(0, Math.round(s || 0))
  return String(Math.floor(s / 60)).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0')
}
// «12:30» → 750 с; «1:02:03» → 3723 с. Пусто → null, неверно → NaN.
export function parseT(str) {
  const s = String(str == null ? '' : str).trim().replace(/[.,]/g, ':')
  if (!s) return null
  const p = s.split(':')
  if (p.length < 2 || p.length > 3 || !p.every((x) => /^\d{1,3}$/.test(x))) return NaN
  const n = p.map(Number)
  if (n[n.length - 1] > 59 || (p.length === 3 && n[1] > 59)) return NaN
  return p.length === 2 ? n[0] * 60 + n[1] : n[0] * 3600 + n[1] * 60 + n[2]
}
const BAD_TIME = 'Время вводится так: 12:30 (минуты и секунды).'
const REASONS = ['Слишком длинно', 'Только формулы', 'Нет эмоций', 'Обрывок', 'Не показывает преподавателя', 'Другое']

export function useLessonMoments({ recPath, teacher, videoRef, active, durationSec }) {
  const [types, setTypes] = useState([])
  const [mine, setMine] = useState([])
  const [ai, setAi] = useState([])
  const [judged, setJudged] = useState([])
  const [sa, setSa] = useState(null)
  const [sb, setSb] = useState(null)
  const [taText, setTaText] = useState('')
  const [tbText, setTbText] = useState('')
  const [type, setType] = useState(null)
  const [title, setTitle] = useState('')
  const [msg, setMsg] = useState('')
  const [cur, setCur] = useState(0)
  const [dur, setDur] = useState(durationSec || 0)
  const [editing, setEditing] = useState(null)
  const [openWhy, setOpenWhy] = useState(null)
  const stopAt = useRef(null)

  useEffect(() => {
    if (!active || !recPath) return
    let alive = true
    api.get('/api/moments', { params: { path: recPath } }).then((r) => {
      if (!alive) return
      setTypes(r.data.types || []); setMine(r.data.mine || []); setAi(r.data.ai || []); setJudged(r.data.judged || [])
    }).catch((e) => { if (alive) setMsg(e.response?.data?.error || e.message) })
    return () => { alive = false }
  }, [recPath, active])

  useEffect(() => {
    if (!active) return
    const v = videoRef.current
    if (!v) return
    const onTime = () => {
      setCur(v.currentTime)
      if (stopAt.current != null && v.currentTime >= stopAt.current) { v.pause(); stopAt.current = null }
    }
    const onMeta = () => { if (v.duration && isFinite(v.duration)) setDur(v.duration) }
    v.addEventListener('timeupdate', onTime)
    v.addEventListener('loadedmetadata', onMeta)
    onMeta()
    return () => { v.removeEventListener('timeupdate', onTime); v.removeEventListener('loadedmetadata', onMeta) }
  }, [active, videoRef])

  const D = dur || durationSec || 1
  const now = () => (videoRef.current ? videoRef.current.currentTime : 0)
  const seek = useCallback((t) => {
    const v = videoRef.current
    if (v) v.currentTime = Math.max(0, Math.min(D, t))
  }, [videoRef, D])
  const playSeg = (a, b) => { const v = videoRef.current; if (!v) return; v.currentTime = a; stopAt.current = b; v.play() }

  const setStart = (t) => { setSa(t); setTaText(fmtT(t)); if (sb != null && sb <= t) { setSb(null); setTbText('') } }
  const markStart = () => { setMsg(''); setStart(now()) }
  const markEnd = () => {
    if (sa == null) { setMsg('Сначала отметьте начало момента.'); return }
    const t = now()
    if (t <= sa) { setMsg('Конец должен быть позже начала.'); return }
    setMsg(''); setSb(t); setTbText(fmtT(t))
  }
  const applyTime = (which) => {
    const text = which === 'a' ? taText : tbText
    const t = parseT(text)
    if (t === null) { if (which === 'a') { setSa(null); setTaText('') } else { setSb(null); setTbText('') } setMsg(''); return }
    if (Number.isNaN(t)) { setMsg(BAD_TIME); return }
    if (t > D) { setMsg('В этом уроке только ' + fmtT(D) + '.'); return }
    if (which === 'a') { setStart(t); seek(t) }
    else {
      if (sa == null) { setMsg('Сначала укажите начало.'); return }
      if (t <= sa) { setMsg('Конец должен быть позже начала.'); return }
      setSb(t); setTbText(fmtT(t))
    }
    setMsg('')
  }
  const preview = () => {
    if (sa == null || sb == null) { setMsg('Отметьте начало и конец.'); return }
    playSeg(sa, sb)
  }
  const save = async () => {
    if (sa == null || sb == null) { setMsg('Отметьте начало и конец момента.'); return }
    if (!type) { setMsg('Выберите тип момента.'); return }
    if (!title.trim()) { setMsg('Напишите короткое название.'); return }
    try {
      const r = await api.post('/api/moments', {
        path: recPath, teacher_mk_id: teacher && teacher.mkId, teacher_name: teacher && teacher.name,
        a: sa, b: sb, type, title: title.trim(),
      })
      setMine((m) => [...m, r.data].sort((x, y) => x.a - y.a))
      setSa(null); setSb(null); setTaText(''); setTbText(''); setType(null); setTitle(''); setMsg('Момент сохранён.')
    } catch (e) { setMsg(e.response?.data?.error || e.message) }
  }
  const remove = async (id) => {
    try { await api.delete('/api/moments/' + id); setMine((m) => m.filter((x) => x.id !== id)) }
    catch (e) { setMsg(e.response?.data?.error || e.message) }
  }
  const approve = async (id) => {
    try { const r = await api.post('/api/moments/' + id + '/status', { status: 'Одобрен' }); setMine((m) => m.map((x) => (x.id === id ? r.data : x))) }
    catch (e) { setMsg(e.response?.data?.error || e.message) }
  }
  const saveTime = async (id, aText, bText) => {
    const a = parseT(aText), b = parseT(bText)
    if (a == null || b == null || Number.isNaN(a) || Number.isNaN(b)) return BAD_TIME
    if (b <= a) return 'Конец должен быть позже начала.'
    if (b > D) return 'В этом уроке только ' + fmtT(D) + '.'
    try {
      const r = await api.post('/api/moments/' + id + '/time', { a, b })
      setMine((m) => m.map((x) => (x.id === id ? r.data : x)).sort((x, y) => x.a - y.a)); setEditing(null)
      return ''
    } catch (e) { return e.response?.data?.error || e.message }
  }
  const judge = async (idx, verdict, reason) => {
    try {
      await api.post('/api/moments/judge', { path: recPath, idx, verdict, reason })
      setJudged((j) => [...j, idx]); setOpenWhy(null)
      if (verdict === 'ok') {
        const m = ai[idx]
        setSa(m.a); setSb(m.b); setTaText(fmtT(m.a)); setTbText(fmtT(m.b)); setTitle(m.t || ''); seek(m.a)
        setMsg('Выберите тип и сохраните момент.')
      }
    } catch (e) { setMsg(e.response?.data?.error || e.message) }
  }

  // клавиши: I — начало, O — конец, стрелки ±5 с (не мешаем набору текста)
  useEffect(() => {
    if (!active) return
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return
      const k = e.key
      if (k === 'i' || k === 'I' || k === 'ш' || k === 'Ш') markStart()
      else if (k === 'o' || k === 'O' || k === 'щ' || k === 'Щ') markEnd()
      else if (k === 'ArrowLeft') { seek(now() - 5); e.preventDefault() }
      else if (k === 'ArrowRight') { seek(now() + 5); e.preventDefault() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  return {
    types, mine, ai, judged, sa, sb, taText, setTaText, tbText, setTbText, type, setType, title, setTitle,
    msg, cur, D, editing, setEditing, openWhy, setOpenWhy,
    seek, playSeg, markStart, markEnd, applyTime, preview, save, remove, approve, saveTime, judge, now,
  }
}

// ── интерфейс ────────────────────────────────────────────────────────────
const C = { s100: '#F1F5F9', s200: '#E2E8F0', s400: '#94A3B8', s500: '#64748B', s700: '#334155', s800: '#1E293B', sky700: '#0369A1', sky50: '#F0F9FF', sky100: '#E0F2FE' }
const btn = { border: '1px solid ' + C.s200, background: '#fff', borderRadius: 8, padding: '4px 9px', fontSize: 11.5, fontWeight: 600, color: C.s700, cursor: 'pointer' }
const btnPri = { ...btn, background: C.sky700, borderColor: C.sky700, color: '#fff' }
const timeInput = { width: 84, border: '1px solid ' + C.s200, borderRadius: 10, padding: '7px 9px', fontSize: 14, outline: 'none', fontVariantNumeric: 'tabular-nums', color: '#0F172A' }

export function MomentsTimeline({ mm }) {
  const D = mm.D
  const onClick = (e) => {
    const r = e.currentTarget.getBoundingClientRect()
    mm.seek(((e.clientX - r.left) / r.width) * D)
  }
  return (
    <div style={{ flexShrink: 0, padding: '10px 12px 2px', borderTop: '1px solid ' + C.s800 }}>
      <div onClick={onClick} style={{ position: 'relative', height: 34, background: C.s800, borderRadius: 8, cursor: 'pointer', overflow: 'hidden' }}>
        {mm.ai.map((m, i) => mm.judged.includes(i) ? null : (
          <div key={'a' + i} style={{ position: 'absolute', top: 0, bottom: 0, left: (m.a / D * 100) + '%', width: ((m.b - m.a) / D * 100) + '%', background: 'rgba(56,189,248,.30)', borderLeft: '2px solid #38bdf8' }} />
        ))}
        {mm.mine.map((m) => (
          <div key={m.id} style={{ position: 'absolute', top: 12, bottom: 0, left: (m.a / D * 100) + '%', width: Math.max(0.4, (m.b - m.a) / D * 100) + '%', background: '#10B981', borderRadius: '3px 3px 0 0' }} />
        ))}
        {mm.sa != null && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: (mm.sa / D * 100) + '%', width: Math.max(0.3, ((mm.sb != null ? mm.sb : Math.max(mm.cur, mm.sa)) - mm.sa) / D * 100) + '%', background: 'rgba(251,191,36,.45)', border: '1px solid #FBBF24' }} />
        )}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: (mm.cur / D * 100) + '%', width: 2, background: '#fff' }} />
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: C.s400, padding: '6px 0 8px' }}>
        <span><i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 5, background: 'rgba(56,189,248,.5)' }} />подсказки ИИ</span>
        <span><i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 5, background: '#10B981' }} />отмеченные моменты</span>
        <span><i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 5, background: 'rgba(251,191,36,.6)' }} />выбираю сейчас</span>
      </div>
    </div>
  )
}

export function MomentsMarkButtons({ mm, onOpenTab }) {
  const st = { background: '#064E3B', color: '#A7F3D0', borderRadius: 8, padding: '6px 12px', fontSize: 12.5, border: 0, cursor: 'pointer' }
  return (
    <>
      <span style={{ flex: 1 }} />
      <button style={st} onClick={() => { onOpenTab(); mm.markStart() }}>Начало момента</button>
      <button style={st} onClick={() => { onOpenTab(); mm.markEnd() }}>Конец момента</button>
    </>
  )
}

export function MomentsTab({ mm }) {
  const [eA, setEA] = useState('')
  const [eB, setEB] = useState('')
  const [eMsg, setEMsg] = useState('')
  const startEdit = (m) => { setEA(fmtT(m.a)); setEB(fmtT(m.b)); setEMsg(''); mm.setEditing(m.id) }
  const onEnter = (fn) => (e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); fn && fn() } }
  const restAi = mm.ai.map((m, i) => ({ m, i })).filter((x) => !mm.judged.includes(x.i))
  const card = { border: '1px solid ' + C.s200, borderRadius: 12, padding: '9px 11px', marginBottom: 7 }
  const lbl = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: C.s500, fontWeight: 600 }
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
      <div style={{ borderRadius: 12, padding: '8px 10px', marginBottom: 10, fontSize: 11.5, lineHeight: 1.35, background: C.sky50, border: '1px solid ' + C.sky100, color: C.sky700 }}>
        Видите, как преподаватель объясняет, мотивирует или рассказывает о себе — отметьте этот кусок. Лучшие пойдут на его карточку и в соцсети. Голос и лицо ученика будут скрыты.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', margin: '6px 0' }}>
        <label style={lbl}>Начало
          <input style={timeInput} value={mm.taText} placeholder="мм:сс" maxLength={8} inputMode="numeric"
            onChange={(e) => mm.setTaText(e.target.value)} onBlur={() => mm.applyTime('a')} onKeyDown={onEnter()} />
        </label>
        <label style={lbl}>Конец
          <input style={timeInput} value={mm.tbText} placeholder="мм:сс" maxLength={8} inputMode="numeric"
            onChange={(e) => mm.setTbText(e.target.value)} onBlur={() => mm.applyTime('b')} onKeyDown={onEnter()} />
        </label>
        <button style={{ ...btn, padding: '8px 10px' }} onClick={mm.preview}>Посмотреть</button>
      </div>
      <div style={{ fontSize: 11, color: C.s400, lineHeight: 1.35, margin: '0 0 4px' }}>
        Впишите время руками, например 12:30, или нажмите «Начало момента» и «Конец момента» под плеером.
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
        {mm.types.map((t) => (
          <button key={t} onClick={() => mm.setType(t)} style={{
            border: '1px solid ' + (mm.type === t ? '#0284C7' : C.s200), background: mm.type === t ? C.sky50 : '#fff',
            color: mm.type === t ? C.sky700 : C.s700, fontWeight: mm.type === t ? 700 : 400,
            borderRadius: 999, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>{t}</button>
        ))}
      </div>
      <input value={mm.title} onChange={(e) => mm.setTitle(e.target.value)} maxLength={80}
        placeholder="Название в 2–4 слова, например: Снимает страх"
        style={{ width: '100%', border: '1px solid ' + C.s200, borderRadius: 12, padding: '8px 10px', fontSize: 12.5, outline: 'none' }} />
      <button onClick={mm.save} style={{ width: '100%', background: C.sky700, color: '#fff', border: 0, borderRadius: 10, padding: 10, fontWeight: 700, fontSize: 13, marginTop: 8, cursor: 'pointer' }}>
        Сохранить момент
      </button>
      <div style={{ fontSize: 12, color: C.sky700, minHeight: 16, marginTop: 6 }}>{mm.msg}</div>

      <div style={{ fontSize: 12, fontWeight: 700, margin: '16px 0 6px', color: C.s700 }}>Мои отметки</div>
      {mm.mine.length === 0 && <div style={{ color: C.s400, fontSize: 12.5, textAlign: 'center', padding: '14px 4px' }}>Пока нет. Отметьте начало и конец на полоске.</div>}
      {mm.mine.map((m) => (
        <div key={m.id} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
            <b style={{ fontSize: 12.5 }}>{m.title}</b>
            <span onClick={() => mm.seek(m.a)} style={{ color: C.sky700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>{fmtT(m.a)}–{fmtT(m.b)}</span>
          </div>
          <div style={{ fontSize: 12, color: C.s500, margin: '3px 0 7px' }}>{m.type}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, borderRadius: 999, padding: '1px 8px', fontWeight: 600, background: m.status === 'Одобрен' ? '#D1FAE5' : '#FEF3C7', color: m.status === 'Одобрен' ? '#065F46' : '#92400E' }}>{m.status}</span>
            <button style={btn} onClick={() => mm.playSeg(m.a, m.b)}>Смотреть</button>
            <button style={btn} onClick={() => startEdit(m)}>Править время</button>
            {m.status === 'Предложен' && <button style={btn} onClick={() => mm.approve(m.id)}>Одобрить</button>}
            <button style={btn} onClick={() => mm.remove(m.id)}>Убрать</button>
          </div>
          {mm.editing === m.id && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed ' + C.s200 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <label style={lbl}>Начало<input style={timeInput} value={eA} onChange={(e) => setEA(e.target.value)} maxLength={8} inputMode="numeric" /></label>
                <button style={btn} onClick={() => setEA(fmtT(mm.now()))}>с плеера</button>
                <label style={lbl}>Конец<input style={timeInput} value={eB} onChange={(e) => setEB(e.target.value)} maxLength={8} inputMode="numeric" /></label>
                <button style={btn} onClick={() => setEB(fmtT(mm.now()))}>с плеера</button>
                <button style={btnPri} onClick={async () => setEMsg(await mm.saveTime(m.id, eA, eB))}>Сохранить время</button>
                <button style={btn} onClick={() => mm.setEditing(null)}>Отмена</button>
              </div>
              <div style={{ fontSize: 12, color: C.sky700, minHeight: 16, marginTop: 6 }}>{eMsg}</div>
            </div>
          )}
        </div>
      ))}

      <div style={{ fontSize: 12, fontWeight: 700, margin: '16px 0 6px', color: C.s700, display: 'flex', justifyContent: 'space-between' }}>
        <span>Подсказки ИИ</span><span style={{ fontWeight: 400, color: C.s400 }}>{restAi.length} шт</span>
      </div>
      {restAi.length === 0 && <div style={{ color: C.s400, fontSize: 12.5, textAlign: 'center', padding: '14px 4px' }}>
        {mm.ai.length ? 'Подсказки закончились.' : 'Для этой записи подсказок ИИ нет — отмечайте сами.'}</div>}
      {restAi.map(({ m, i }) => (
        <div key={i} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
            <b style={{ fontSize: 12.5 }}>{m.t}</b>
            <span onClick={() => mm.seek(m.a)} style={{ color: C.sky700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>{fmtT(m.a)}–{fmtT(m.b)}</span>
          </div>
          <div style={{ fontSize: 12, color: C.s500, margin: '3px 0 7px', lineHeight: 1.35 }}>«{m.q}»</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button style={btn} onClick={() => mm.playSeg(m.a, m.b)}>Смотреть</button>
            <button style={btnPri} onClick={() => mm.judge(i, 'ok')}>Подходит</button>
            <button style={btn} onClick={() => mm.setOpenWhy(mm.openWhy === i ? null : i)}>Не подходит</button>
          </div>
          {mm.openWhy === i && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>
              {REASONS.map((r) => (
                <button key={r} onClick={() => mm.judge(i, 'no', r)} style={{ border: '1px solid ' + C.s200, background: '#fff', borderRadius: 999, padding: '4px 9px', fontSize: 11.5, cursor: 'pointer' }}>{r}</button>
              ))}
            </div>
          )}
        </div>
      ))}
      <div style={{ fontSize: 11, color: C.s400, marginTop: 10 }}>Клавиши: I — начало, O — конец, ← → — на 5 секунд.</div>
    </div>
  )
}
